/**
 * Public /delete-account route — Google Play "Account Deletion URL" target.
 *
 * - Logged out: explains that deletion requires signing in, with a CTA that
 *   returns the user to /delete-account after login.
 * - Logged in: renders the EXISTING DeleteAccountDialog (same UI + backend
 *   flow as Profile → Konto löschen). No functional changes.
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import DeleteAccountDialog from "../components/DeleteAccountDialog";
import { useTranslation } from "react-i18next";

export default function DeleteAccountPage() {
  const { t } = useTranslation("profile");
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div
        className="mx-auto max-w-md py-16 text-center text-sm text-zinc-500"
        data-testid="delete-account-page-loading"
      >
        {t("deletePage.loading")}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-md py-12" data-testid="delete-account-page">
        <div className="rounded-3xl border border-zinc-800 bg-zinc-900/40 p-8 space-y-5 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-500/10 text-rose-300">
            <ShieldAlert size={24} />
          </div>
          <h1 className="text-2xl font-semibold text-zinc-100">{t("deletePage.title")}</h1>
          <p
            className="text-sm text-zinc-400 leading-relaxed"
            data-testid="delete-account-login-required"
          >
            {t("deletePage.loginRequired")}
          </p>
          <Link
            to="/login"
            state={{ from: { pathname: "/delete-account" } }}
            data-testid="delete-account-login-cta"
            className="inline-block rounded-md bg-emerald-500 px-5 py-2.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400"
          >
            {t("deletePage.toLogin")}
          </Link>
          <p className="text-xs text-zinc-600">
            {t("deletePage.noAccount")}{" "}
            <Link
              to="/register"
              className="text-emerald-400 underline-offset-4 hover:underline"
            >
              {t("deletePage.register")}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Logged in — show the existing delete flow (identical to Profile page).
  return (
    <div className="mx-auto max-w-md py-12" data-testid="delete-account-page">
      <div className="rounded-3xl border border-zinc-800 bg-zinc-900/40 p-8 space-y-3 text-center">
        <h1 className="text-2xl font-semibold text-zinc-100">{t("deletePage.title")}</h1>
        <p className="text-sm text-zinc-400">
          {t("deletePage.signedInAs")} <span className="text-zinc-200">{user.email}</span>.
        </p>
        <p className="text-xs text-zinc-500">
          {t("deletePage.followSteps")}
        </p>
      </div>
      <DeleteAccountDialog open onClose={() => navigate("/profile")} />
    </div>
  );
}
